import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FiChevronLeft, FiPackage, FiMapPin, FiCreditCard,
} from 'react-icons/fi';
import orderService from '../../services/order.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import OrderTimeline from '../../components/order/OrderTimeline';
import ContactSellerButton from '../../components/chat/ContactSellerButton';

const OrderDetailPage = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const { data } = await orderService.getById(id);
        setOrder(data.data);
      } catch (error) {
        console.error('Failed to fetch order:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  if (loading) return <LoadingSpinner />;

  if (!order) {
    return (
      <div className="section-padding text-center">
        <h3>Order not found</h3>
        <Link to="/account/orders" className="btn btn-ebw-primary mt-3">Back to Orders</Link>
      </div>
    );
  }

  const items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
  const shippingAddress = typeof order.shippingAddress === 'string'
    ? JSON.parse(order.shippingAddress) : order.shippingAddress;

  const isCancelled = order.status === 'cancelled' || order.status === 'refunded';

  return (
    <div className="section-padding">
      <div className="container" style={{ maxWidth: '900px' }}>
        {/* Header */}
        <Link to="/account/orders" className="text-decoration-none text-muted d-inline-flex align-items-center gap-1 mb-3">
          <FiChevronLeft /> Back to Orders
        </Link>

        <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
          <div>
            <h2 className="mb-1">Order {order.orderNumber}</h2>
            <p className="text-muted mb-0">
              Placed on {new Date(order.createdAt).toLocaleDateString('en-US', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
              })}
            </p>
          </div>
          <div className="text-end">
            <span className={`badge bg-${isCancelled ? 'danger' : 'primary'} fs-6`}>
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </span>
            <div className="mt-2">
              <a
                href={`${import.meta.env.VITE_API_URL || '/api'}/v1/orders/${order.id}/invoice`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-ebw-outline"
              >
                📄 Invoice
              </a>
              {order.items?.[0]?.merchantId && (
                <div className="mt-2">
                  <ContactSellerButton
                    merchantId={order.items[0].merchantId}
                    orderId={order.id}
                    variant="link"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order Tracking Timeline */}
        <div className="dashboard-card mb-4">
          <OrderTimeline order={order} />
          {order.trackingNumber && order.shippingCarrier && (
            <div className="mt-3 p-3" style={{ background: '#F0FDF4', borderRadius: '8px', border: '1px solid #BBF7D0' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#059669', marginBottom: '4px' }}>
                🚚 Tracking Information
              </div>
              <div style={{ fontSize: '13px' }}>
                <strong>Carrier:</strong> {order.shippingCarrier} &nbsp;·&nbsp;
                <strong>Tracking #:</strong> {order.trackingNumber}
              </div>
              <a
                href={
                  order.shippingCarrier?.toLowerCase().includes('fedex') ? `https://www.fedex.com/fedextrack/?trknbr=${order.trackingNumber}` :
                  order.shippingCarrier?.toLowerCase().includes('ups') ? `https://www.ups.com/track?tracknum=${order.trackingNumber}` :
                  `https://tools.usps.com/go/TrackConfirmAction?tLabels=${order.trackingNumber}`
                }
                target="_blank" rel="noopener noreferrer"
                className="btn btn-sm btn-outline-success mt-2"
                style={{ fontSize: '12px' }}
              >
                📍 Track Package
              </a>
            </div>
          )}
        </div>

        <div className="row g-4">
          {/* Order Items */}
          <div className="col-lg-7">
            <div className="dashboard-card">
              <h5 className="mb-3">Items ({items?.length || 0})</h5>
              {items?.map((item, index) => (
                <div key={index} className={`d-flex gap-3 py-3 ${index > 0 ? 'border-top' : ''}`}>
                  <img
                    src={item.image || '/placeholder.png'}
                    alt={item.name}
                    className="rounded"
                    style={{ width: '70px', height: '70px', objectFit: 'cover' }}
                  />
                  <div className="flex-grow-1">
                    <h6 className="mb-1">{item.name}</h6>
                    <small className="text-muted">Qty: {item.quantity} × ${parseFloat(item.price).toFixed(2)}</small>
                  </div>
                  <strong>${(item.quantity * parseFloat(item.price)).toFixed(2)}</strong>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="col-lg-5">
            {/* Payment Summary */}
            <div className="dashboard-card mb-3">
              <h5 className="mb-3">Payment Summary</h5>
              <div className="d-flex justify-content-between mb-2">
                <span>Subtotal</span>
                <span>${parseFloat(order.subtotal).toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Tax</span>
                <span>${parseFloat(order.tax).toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Shipping</span>
                <span>
                  {parseFloat(order.shippingCost) === 0
                    ? <span className="text-success">FREE</span>
                    : `$${parseFloat(order.shippingCost).toFixed(2)}`}
                </span>
              </div>
              {parseFloat(order.discount) > 0 && (
                <div className="d-flex justify-content-between mb-2 text-success">
                  <span>Discount</span>
                  <span>-${parseFloat(order.discount).toFixed(2)}</span>
                </div>
              )}
              <hr />
              <div className="d-flex justify-content-between">
                <strong>Total</strong>
                <strong className="text-primary fs-5">${parseFloat(order.totalAmount).toFixed(2)}</strong>
              </div>

              <div className="mt-3 pt-3 border-top">
                <div className="d-flex align-items-center gap-2">
                  <FiCreditCard className="text-muted" />
                  <span className="text-muted small">
                    Payment: <span className={`badge bg-${order.paymentStatus === 'paid' ? 'success' : 'warning'}`}>
                      {order.paymentStatus}
                    </span>
                  </span>
                </div>
                {order.paymentMethod && (
                  <small className="text-muted">Method: {order.paymentMethod}</small>
                )}
                {order.paidAt && (
                  <small className="text-muted d-block">
                    Paid on {new Date(order.paidAt).toLocaleDateString()}
                  </small>
                )}
              </div>
            </div>

            {/* Shipping Address */}
            {shippingAddress && (
              <div className="dashboard-card">
                <h5 className="mb-3">
                  <FiMapPin className="me-2" />Shipping Address
                </h5>
                <p className="mb-0">
                  {shippingAddress.fullName && <>{shippingAddress.fullName}<br /></>}
                  {shippingAddress.street}<br />
                  {shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}<br />
                  {shippingAddress.country}
                </p>
                {shippingAddress.phone && (
                  <p className="mb-0 mt-2 text-muted small">Phone: {shippingAddress.phone}</p>
                )}
              </div>
            )}

            {order.notes && (
              <div className="dashboard-card mt-3">
                <h6>Order Notes</h6>
                <p className="text-muted mb-0">{order.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
